import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { ReferenceService } from '../../../../generated/auth/services/reference.service';
import { loadListFailure, loadListSuccess, loadTitleList, toggleLoadingStatus } from './store.actions';
import { selectTitleIsLoading, selectTitleList } from './store.selectors';

@Injectable()
export class TitleEffect {
  private readonly action = inject(Actions);
  private readonly store = inject(Store);
  private readonly service = inject(ReferenceService);

  constructor() {}

  loadList = createEffect(() =>
    this.action.pipe(
      ofType(loadTitleList),
      withLatestFrom(this.store.select(selectTitleList), this.store.select(selectTitleIsLoading)),
      filter(([action, state, status]) => !state?.length && !status),
      tap(() => this.store.dispatch(toggleLoadingStatus({ status: true }))),
      switchMap((action) =>
        this.service.referenceLookupGetTitlesGet().pipe(
          switchMap((response) => {
            const list = (response?.result ?? []).slice();
            const collator = new Intl.Collator('en', { sensitivity: 'base' });
            const priority = ['mr', 'mrs', 'ms', 'master', 'miss'];
            const normalize = (s: string) => s.toLowerCase().replace(/\./g, '').trim();

            list.sort((a: any, b: any) => {
              const aName = (a?.name ?? '').trim();
              const bName = (b?.name ?? '').trim();
              const aNorm = normalize(aName);
              const bNorm = normalize(bName);
              const aRank = priority.indexOf(aNorm);
              const bRank = priority.indexOf(bNorm);
              const aIsPriority = aRank !== -1;
              const bIsPriority = bRank !== -1;

              if (aIsPriority && bIsPriority) return aRank - bRank;
              if (aIsPriority && !bIsPriority) return -1;
              if (!aIsPriority && bIsPriority) return 1;
              return collator.compare(aName, bName);
            });

            return of(loadListSuccess({ payload: list }));
          }),
          catchError((error) => of(loadListFailure({ error }))),
        ),
      ),
    ),
  );
}
